import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Users, Trash2, Eye, Shield, MapPin, Phone, Calendar, FileImage, 
  User as UserIcon, CreditCard, Globe
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface User {
  id: string;
  full_name: string;
  email: string;
  role: string;
  kyc_status: string;
  created_at: string;
  phone?: string;
  national_id?: string;
  gender?: string;
  address_country?: string;
  address_city?: string;
  address_area?: string;
  address_street?: string;
  address_building?: string;
  address_apt?: string;
  id_front_url?: string;
  id_back_url?: string;
  avatar_url?: string;
  language?: string;
  university_id?: string;
  budget_max?: number;
  roommates_count?: number;
  expected_move_in?: string;
  property_ownership_type?: string;
  payout_method?: string;
  updated_at?: string;
}

interface UserCardViewProps {
  users: User[];
  onViewUser: (user: User) => void;
  onUpdateRole: (userId: string, newRole: string) => void;
  onDeleteUser: (userId: string) => void;
  isDeleting: boolean;
}

const UserCardView = ({ users, onViewUser, onUpdateRole, onDeleteUser, isDeleting }: UserCardViewProps) => {
  const { t } = useLanguage();

  const formatAddress = (user: User) => {
    const parts = [
      user.address_building,
      user.address_street,
      user.address_area,
      user.address_city,
      user.address_country
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Not provided';
  };

  const viewDocument = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin': return 'destructive';
      case 'landlord': return 'default';
      case 'student': return 'secondary';
      default: return 'outline';
    }
  };

  const getKYCBadgeVariant = (status: string) => {
    switch (status) {
      case 'VERIFIED': return 'default';
      case 'PENDING': return 'secondary';
      case 'REJECTED': return 'destructive';
      case 'UNDER_REVIEW': return 'outline';
      default: return 'outline';
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
      {users.map((user) => (
        <Card key={user.id} className="hover:shadow-md transition-all duration-200 border hover:border-primary/30">
          <CardContent className="p-4">
            <div className="space-y-3">
              {/* User Header - Compact */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-base truncate">{user.full_name || 'Unnamed User'}</h3>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Badge variant={getRoleBadgeVariant(user.role)} className="text-xs px-2 py-0.5">
                    {user.role?.toUpperCase()}
                  </Badge>
                  <Badge variant={getKYCBadgeVariant(user.kyc_status)} className="text-xs px-2 py-0.5">
                    {user.kyc_status}
                  </Badge>
                </div>
              </div>

              {/* User Details - Compact Grid */}
              <div className="grid grid-cols-1 gap-1.5 text-xs">
                {/* Contact Info Row */}
                <div className="flex items-center justify-between">
                  {user.phone && (
                    <div className="flex items-center gap-1 text-muted-foreground flex-1 min-w-0">
                      <Phone className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{user.phone}</span>
                    </div>
                  )}
                  {user.national_id && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <CreditCard className="h-3 w-3" />
                      <span>***{user.national_id.slice(-4)}</span>
                    </div>
                  )}
                </div>

                {/* Address Row */}
                <div className="flex items-start gap-1 text-muted-foreground">
                  <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span className="text-xs line-clamp-1">{formatAddress(user)}</span>
                </div>

                {/* Additional Info Row */}
                <div className="flex items-center justify-between">
                  {user.language && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Globe className="h-3 w-3" />
                      <span className="capitalize">{user.language}</span>
                    </div>
                  )}
                  {user.gender && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <UserIcon className="h-3 w-3" />
                      <span className="capitalize">{user.gender}</span>
                    </div>
                  )}
                </div>

                {/* Date Info Row */}
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  <span className="text-xs">
                    {new Date(user.created_at).toLocaleDateString()}
                  </span>
                  {user.updated_at && (
                    <span className="text-xs">
                      • {new Date(user.updated_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              {/* KYC Documents Section - Compact */}
              {(user.id_front_url || user.id_back_url) && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1 text-xs font-medium">
                    <Shield className="h-3 w-3" />
                    <span>{t('kyc_documents', 'وثائق التحقق', 'KYC Documents')}</span>
                  </div>
                  <div className="flex gap-1">
                    {user.id_front_url && (
                      <button 
                        className="flex-1 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md p-1.5 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900 transition-colors"
                        onClick={() => viewDocument(user.id_front_url!)}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <FileImage className="h-3 w-3 text-green-600" />
                          <span className="text-xs text-green-700 dark:text-green-300 font-medium">
                            {t('front_id', 'أمامية', 'Front')}
                          </span>
                        </div>
                      </button>
                    )}
                    {user.id_back_url && (
                      <button 
                        className="flex-1 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md p-1.5 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900 transition-colors"
                        onClick={() => viewDocument(user.id_back_url!)}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <FileImage className="h-3 w-3 text-green-600" />
                          <span className="text-xs text-green-700 dark:text-green-300 font-medium">
                            {t('back_id', 'خلفية', 'Back')}
                          </span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons - Compact */}
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-muted">
                <Select
                  value={user.role}
                  onValueChange={(newRole) => onUpdateRole(user.id, newRole)}
                >
                  <SelectTrigger className="w-20 h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">{t('student', 'طالب', 'Student')}</SelectItem>
                    <SelectItem value="landlord">{t('landlord', 'مالك', 'Landlord')}</SelectItem>
                    <SelectItem value="admin">{t('admin', 'مدير', 'Admin')}</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewUser(user)}
                    className="h-7 px-2"
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 h-7 px-2"
                        disabled={isDeleting}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {t('delete_user', 'حذف المستخدم', 'Delete User')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('delete_user_confirm', 
                            'هل أنت متأكد من حذف هذا المستخدم؟ هذا الإجراء لا يمكن التراجع عنه.',
                            `Are you sure you want to delete ${user.full_name}? This action cannot be undone and will permanently remove the user from both the website and database.`
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>
                          {t('cancel', 'إلغاء', 'Cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onDeleteUser(user.id)}
                          className="bg-red-600 hover:bg-red-700"
                          disabled={isDeleting}
                        >
                          {isDeleting ? 'Deleting...' : t('delete', 'حذف', 'Delete')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      
      {users.length === 0 && (
        <div className="col-span-full text-center py-8 text-muted-foreground">
          {t('no_users_found', 'لم يتم العثور على مستخدمين', 'No users found')}
        </div>
      )}
    </div>
  );
};

export default UserCardView;